import React, { useState } from 'react';
import { MailIcon, Phone, User, MessageSquare } from 'lucide-react';

const EMAILJS_CONFIG = {
  serviceId: import.meta.env.VITE_EMAILJS_SERVICE_ID,
  templateId: import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
  publicKey: import.meta.env.VITE_EMAILJS_PUBLIC_KEY,
};

const ContactForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
    query: '',
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templateId,
        {
          from_name: formData.fullName,
          from_email: formData.email,
          phone_number: formData.phoneNumber,
          message: formData.query,
        },
        EMAILJS_CONFIG.publicKey
      );

      if (response.status === 200) {
        setStatus({
          type: 'success',
          message: 'Thank you! We will contact you shortly.'
        });
        setFormData({
          fullName: '',
          phoneNumber: '',
          email: '',
          query: '',
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Something went wrong. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const InputWrapper = ({ label, children }) => (
    <div className="relative flex-1 min-w-[250px]">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      {children}
    </div>
  );

  const StatusAlert = ({ type, message }) => {
    if (!message) return null;
    
    const alertStyles = type === 'error'
      ? 'bg-red-50 text-red-800 border-red-200'
      : 'bg-green-50 text-green-800 border-green-200';

    return (
      <div className={`mt-4 p-4 rounded-lg border ${alertStyles} animate-fadeIn`}>
        {message}
      </div>
    );
  };

  return (
    <section className="w-full min-h-screen bg-gradient-to-br from-blue-600 to-blue-400 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Left Section */}
            <div className="p-8 bg-blue-600 text-white flex flex-col justify-between">
              <div className="space-y-8">
                <h2 className="text-4xl font-bold tracking-tight">
                  Let's discuss your project
                </h2>
                <p className="text-blue-100 text-lg">
                  Have questions? We'd love to hear from you. Send us a message
                  and we'll respond as soon as possible.
                </p>
                
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <Phone className="h-6 w-6" />
                    <span>+91 7011523651 </span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <MailIcon className="h-6 w-6" />
                    <span>urbanwallprojects@gmail.com</span>
                  </div>
                </div>
              </div>
              
              <div className="mt-8">
                <p className="text-sm text-blue-100">
                  © 2024 Your Company. All rights reserved.
                </p>
              </div>
            </div>

            {/* Right Section */}
            <div className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="flex flex-wrap gap-4">
                  <InputWrapper label="Full Name">
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        className="pl-10 w-full rounded-lg border border-gray-300 px-4 py-3 
                          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                          transition-colors duration-200"
                        placeholder="Your Name"
                        required
                      />
                    </div>
                  </InputWrapper>

                  <InputWrapper label="Phone Number">
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        className="pl-10 w-full rounded-lg border border-gray-300 px-4 py-3 
                          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                          transition-colors duration-200"
                        placeholder="+91 Your Conatct Number"
                        required
                      />
                    </div>
                  </InputWrapper>
                </div>

                <InputWrapper label="Email Address">
                  <div className="relative">
                    <MailIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="pl-10 w-full rounded-lg border border-gray-300 px-4 py-3 
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                        transition-colors duration-200"
                      placeholder="Your Email"
                      required
                    />
                  </div>
                </InputWrapper>

                <InputWrapper label="Your Message">
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 text-gray-400 h-5 w-5" />
                    <textarea
                      name="query"
                      value={formData.query}
                      onChange={handleChange}
                      rows={4}
                      className="pl-10 w-full rounded-lg border border-gray-300 px-4 py-3 
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                        transition-colors duration-200 resize-y"
                      placeholder="Tell us about your project..."
                      required
                    />
                  </div>
                </InputWrapper>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full rounded-lg bg-blue-600 px-4 py-3 text-white font-medium
                    transition-all duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 
                    focus:ring-blue-500 focus:ring-offset-2
                    disabled:opacity-75 disabled:cursor-not-allowed
                    transform hover:scale-[1.02] active:scale-[0.98]`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>

              <StatusAlert type={status.type} message={status.message} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;